/**
 * HowItWorks — Verkaufsstarker „So funktioniert's"-Block für Trial-User.
 *
 * Zeigt in 5 Schritten den End-to-End-Workflow:
 *   1. Registrieren (30 Sek)
 *   2. Schaden vor Ort aufnehmen (Mobil)
 *   3. Fotos + Sprachaufnahme (KI übernimmt)
 *   4. Wir erstellen das fertige Gutachten
 *   5. Du kassierst das Honorar — wir verdienen nur am Abo
 *
 * Closing-Card erklärt das Win-Win-Modell.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import {
  UserPlus,
  MapPin,
  Camera,
  Sparkles,
  Wallet,
  ArrowRight,
  CheckCircle2,
  Zap,
  Globe,
  Mail,
  ShoppingBag,
  Megaphone,
  FileText,
  PhoneCall,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: UserPlus,
    title: "Registrieren — fertig",
    text: "30 Sekunden bis zum Account. Kein langes Onboarding, kein Vertriebsgespräch. Du startest direkt mit deiner ersten Akte.",
    bullet: "Keine Kreditkarte · sofort einsatzbereit",
  },
  {
    n: "02",
    icon: Globe,
    title: "Eigene Web­site + Profil",
    text: "Mit der Anmeldung bekommst du eine eigene Profilseite auf easygutachter.de und eine E-Mail-Adresse @easygutachter.de.",
    bullet: "Kunden finden dich · vertrauen sofort",
  },
  {
    n: "03",
    icon: MapPin,
    title: "Du fährst zum Schaden",
    text: "Vor Ort öffnest du Easy Gutachter auf dem Handy. Kundendaten, Versicherer, VIN — alles in 3 Minuten erfasst.",
    bullet: "DAT-Lupe lädt Fahrzeugdaten automatisch",
  },
  {
    n: "04",
    icon: Camera,
    title: "Fotos + Sprachaufnahme",
    text: "Du machst die Fotos nach unserer Vorlage, diktierst kurz den Schadenhergang. Die KI erledigt den Rest.",
    bullet: "55+ Bilder gleichzeitig · HEIC-Support",
  },
  {
    n: "05",
    icon: Sparkles,
    title: "Wir erstellen dein Gutachten",
    text: "KI + Backoffice prüfen, formulieren, kalkulieren. Du bekommst das fertige PDF zur finalen Freigabe.",
    bullet: "Durchschnitt: < 24 Std bis zur Endversion",
  },
  {
    n: "06",
    icon: Wallet,
    title: "Du kassierst das Honorar",
    text: "Du stellst die Rechnung direkt aus Easy Gutachter. Honorar geht zu 100 % auf dein Konto.",
    bullet: "Wir verdienen NUR am Abo · null Kommission",
  },
];

// Inkludierte Leistungen (Komplettpaket-Hinweis vor dem Win-Win-Block).
const INCLUDED = [
  { icon: Globe,      label: "Eigene Profilseite",      sub: "deinname.easygutachter.de" },
  { icon: Mail,       label: "E-Mail-Adresse",          sub: "deinname@easygutachter.de" },
  { icon: Megaphone,  label: "Werbematerial",           sub: "Flyer · Karten · Türschild" },
  { icon: ShoppingBag,label: "Branded-Shop",            sub: "Polos, Caps, Give-Aways" },
  { icon: FileText,   label: "Gutachten-Erstellung",    sub: "KI + Backoffice für dich" },
  { icon: PhoneCall,  label: "Priorisierter Support",   sub: "Direkt-Telefon & Chat" },
];

export default function HowItWorks({ onCreateCase, onUpsell, creating }) {
  return (
    <div className="mb-8" data-testid="how-it-works">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto mb-7">
        <div className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-700 rounded-full px-3 py-1.5 mb-3 border border-cyan-200">
          <Zap className="h-3.5 w-3.5" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold">So einfach läuft's bei dir</span>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight tracking-tight">
          Aufnehmen. Abgeben. <span className="text-cyan-600">Honorar kassieren.</span>
        </h2>
        <p className="mt-3 text-base text-zinc-600 leading-relaxed">
          Du registrierst dich — und verdienst ab dem ersten Tag. <strong className="text-zinc-900">Kein Onboarding</strong>, kein Kurs, keine Wartezeit. Du nimmst den Schaden auf — wir machen daraus ein verkaufsfertiges Gutachten.
        </p>
      </div>

      {/* 6-Step Flow */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-7">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.n}
              className="relative rounded-2xl bg-white border border-zinc-200 p-5 hover:shadow-md hover:border-cyan-300 transition group"
              data-testid={`how-it-works-step-${i + 1}`}
            >
              {/* Step number */}
              <div className="absolute -top-3 left-5 bg-zinc-900 text-white text-[10px] tracking-[0.18em] font-bold px-2.5 py-1 rounded-md group-hover:bg-cyan-600 transition">
                {s.n}
              </div>
              <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center mb-3 group-hover:bg-cyan-600 group-hover:text-white transition">
                <Icon className="h-5 w-5" strokeWidth={1.8} />
              </div>
              <div className="font-display font-bold text-sm leading-tight">{s.title}</div>
              <div className="text-xs text-zinc-600 mt-1.5 leading-relaxed">{s.text}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-cyan-700 mt-3 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />
                <span className="truncate">{s.bullet}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Was alles inkludiert ist (Komplettpaket) */}
      <div className="rounded-3xl bg-gradient-to-br from-cyan-50 via-white to-emerald-50/40 border-2 border-cyan-200 p-6 sm:p-8 mb-6" data-testid="how-it-works-included">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-cyan-600" />
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">Alles im Abo enthalten</div>
        </div>
        <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight max-w-2xl">
          Dein <span className="text-cyan-600">Komplettpaket</span> für den Auftritt als Profi-Sachverständiger
        </h3>
        <p className="mt-2 text-sm text-zinc-600 leading-relaxed max-w-2xl">
          Nicht nur die Software — wir geben dir alles, was du brauchst, um sofort professionell aufzutreten und Kunden zu gewinnen.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          {INCLUDED.map(({ icon: Icon, label, sub }) => (
            <div key={label} className="flex items-start gap-3 rounded-xl bg-white border border-zinc-200 p-3.5" data-testid={`included-${label}`}>
              <div className="h-9 w-9 rounded-lg bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4" strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm leading-tight">{label}</div>
                <div className="text-xs text-zinc-500 font-mono mt-0.5 truncate">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Win-Win Closing */}
      <div className="rounded-3xl bg-zinc-900 text-white px-6 py-8 sm:px-10 sm:py-10 relative overflow-hidden" data-testid="how-it-works-closing">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none" />

        <div className="relative grid lg:grid-cols-[1fr_auto] items-center gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-cyan-300 mb-2">Das Win-Win-Modell</div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight max-w-2xl">
              Du verdienst pro Gutachten <span className="text-cyan-400">300 – 800 €</span>.
              Wir verdienen nur unser <span className="text-cyan-400">Abo</span> — keine Provision.
            </h3>
            <p className="mt-3 text-sm sm:text-base text-zinc-300 max-w-2xl leading-relaxed">
              Andere Plattformen behalten 30 % deines Honorars als Vermittlungsgebühr. Bei uns kassierst du
              <strong className="text-white"> 100 %</strong>. Wir verdienen ausschließlich an deinem monatlichen Abo —
              transparent, planbar, fair.
            </p>

            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-xs">
              {[
                "100 % deines Honorars",
                "Keine Provision · keine versteckten Kosten",
                "Du arbeitest auf eigene Rechnung",
                "Wir helfen, du verdienst",
              ].map((t) => (
                <span key={t} className="flex items-center gap-1.5 text-zinc-300">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" strokeWidth={2.4} />
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:items-end">
            <Button
              onClick={onCreateCase}
              disabled={creating}
              size="lg"
              className="bg-white text-zinc-900 hover:bg-cyan-50 font-bold shadow-xl"
              data-testid="how-it-works-create-btn"
            >
              Jetzt Test-Akte starten
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <button
              onClick={onUpsell}
              className="text-xs text-cyan-300 hover:text-white underline-offset-4 hover:underline font-semibold"
              data-testid="how-it-works-plans-btn"
            >
              Oder direkt einen Plan wählen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
